import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useSiteStore from '_stores/site.store'

/**
 * Hook for site-aware navigation
 * Automatically includes siteId in path for site-level routes
 *
 * Usage:
 *   const { navigateToSite, navigateToCorporate } = useSiteNavigation()
 *   navigateToSite('clients')  // → /s/{siteId}/clients
 *   navigateToCorporate('user-access')  // → /c/user-access
 */
export function useSiteNavigation() {
  const navigate = useNavigate()
  const { siteId: paramSiteId } = useParams<{ siteId: string }>()
  const currentSite = useSiteStore((state) => state.currentSite)

  const siteId = paramSiteId || currentSite?.siteId

  /**
   * Navigate to a site-level route
   * Automatically prepends /s/{siteId}/ to the path
   */
  const navigateToSite = useCallback(
    (path: string, options?: { replace?: boolean }) => {
      const cleanPath = path.startsWith('/') ? path.slice(1) : path

      if (!siteId) {
        console.warn(
          '[useSiteNavigation] No siteId available for navigation, redirecting to location selection'
        )
        // Include the intended destination as a redirect parameter
        navigate(`/location-selection?redirect=${encodeURIComponent(cleanPath)}`, options)
        return
      }

      navigate(`/s/${siteId}/${cleanPath}`, options)
    },
    [siteId, navigate]
  )

  /**
   * Navigate to a corporate-level route
   * Automatically prepends /c/ to the path
   */
  const navigateToCorporate = useCallback(
    (path: string, options?: { replace?: boolean }) => {
      // Remove leading slash if present
      const cleanPath = path.startsWith('/') ? path.slice(1) : path
      navigate(`/c/${cleanPath}`, options)
    },
    [navigate]
  )

  /**
   * Build a site-level path without navigating
   * Useful for Link components
   */
  const buildSitePath = useCallback(
    (path: string): string => {
      const cleanPath = path.startsWith('/') ? path.slice(1) : path

      if (!siteId) {
        // Include the intended destination as a redirect parameter
        return `/location-selection?redirect=${encodeURIComponent(cleanPath)}`
      }

      return `/s/${siteId}/${cleanPath}`
    },
    [siteId]
  )

  /**
   * Build a corporate-level path without navigating
   * Useful for Link components
   */
  const buildCorporatePath = useCallback((path: string): string => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path
    return `/c/${cleanPath}`
  }, [])

  return {
    navigateToSite,
    navigateToCorporate,
    buildSitePath,
    buildCorporatePath,
    siteId,
  }
}
